/**
 * Friendly labels + grouping for the `source` slugs we record on each
 * contact submission. Keeping this in one place so the admin table,
 * filters and the weekly tile all stay in sync.
 */

// Friendly titles for known service IDs — overrides the generic prettifier.
const SERVICE_TITLES = {
  // Compliance & Property Services
  "epc-arrangement": "EPC Arrangement",
  "gas-safety-certificate-arrangement": "Gas Safety Certificate",
  "eicr-arrangement": "EICR Arrangement",
  "inventory-preparation": "Inventory Preparation",
  "check-in-service": "Check-In",
  "check-out-service": "Check-Out",
  "property-inspection-service": "Property Inspection",
  "professional-photography": "Professional Photography",
  "key-holding-service": "Key Holding",
  // Tenancy Administration
  "tenancy-variation-and-administration": "Tenancy Variation & Admin",
  "bespoke-landlord-reports": "Bespoke Landlord Reports",
  // Onboarding & Property Transfer
  "management-transfer-existing-tenancy-takeover": "Management Transfer / Takeover",
  "portfolio-onboarding": "Portfolio Onboarding",
  // Attendance & Visits
  "additional-property-attendance": "Additional Property Attendance",
  "landlord-requested-property-visit": "Landlord-Requested Visit",
  // HMO & Licensing
  "hmo-licence-application-assistance": "HMO Licence Application",
  "hmo-licence-renewal": "HMO Licence Renewal",
  "hmo-licence-variation": "HMO Licence Variation",
  "selective-additional-licensing-application": "Selective / Additional Licensing",
  "council-inspection-preparation": "Council Inspection Prep",
  "licensing-compliance-support": "Licensing Compliance Support",
  // Refurbishment & Insurance
  "refurbishment-project-management": "Refurbishment & Project Mgmt",
  "insurance-claim-management": "Insurance Claim Management",
  // Possession & Legal Coordination
  "deposit-dispute-management": "Deposit Dispute Mgmt",
  "section-8-and-possession-notice-service": "Section 8 / Possession",
  "possession-and-legal-case-coordination": "Possession & Legal Coordination",
  "possession-case-coordination": "Possession Case Coordination",
  "court-hearing-attendance-coordination": "Court / Hearing Attendance",
  "bailiff-warrant-coordination": "Bailiff / Warrant Coordination",
  // Reporting & Investment
  "annual-compliance-package": "Annual Compliance",
  "rent-and-legal-protection": "Rent & Legal Protection",
  "property-sourcing-for-investors": "Property Sourcing",
};

// Order matters — the first matching prefix wins.
const SOURCE_RULES = [
  { match: (s) => s === "landlords-page", label: "Landlords (sticky CTA)", group: "Sticky CTA" },
  { match: (s) => s === "property-management-page", label: "Property Mgmt (sticky CTA)", group: "Sticky CTA" },
  { match: (s) => s === "lettings-page", label: "Lettings (sticky CTA)", group: "Sticky CTA" },
  { match: (s) => s.startsWith("package-"), label: (s) => `${capitalise(s.replace("package-", ""))} Package`, group: "Packages" },
  {
    match: (s) => s.startsWith("service-"),
    label: (s) => {
      const id = s.replace("service-", "");
      return `Service: ${SERVICE_TITLES[id] || prettify(id)}`;
    },
    group: "Additional Services",
  },
];

function capitalise(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function prettify(s) {
  return s
    .split("-")
    .map((part) => capitalise(part))
    .join(" ");
}

/**
 * Returns a short, human-friendly label for a source slug.
 * Unknown / missing sources collapse to "Direct".
 */
export function labelForSource(source) {
  if (!source) return "Direct";
  for (const rule of SOURCE_RULES) {
    if (rule.match(source)) {
      return typeof rule.label === "function" ? rule.label(source) : rule.label;
    }
  }
  return source;
}

/**
 * Returns the group bucket for a source slug — used by the weekly tile.
 */
export function groupForSource(source) {
  if (!source) return "Direct";
  for (const rule of SOURCE_RULES) {
    if (rule.match(source)) return rule.group;
  }
  return "Other";
}

/**
 * Sorted list of known additional-service IDs and their friendly labels.
 * Used by the admin per-service sub-filter dropdown.
 */
export const KNOWN_SERVICES = Object.entries(SERVICE_TITLES)
  .map(([id, label]) => ({ id: `service-${id}`, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * The fixed list of group buckets the weekly tile renders, in display order.
 * Showing zeros keeps the layout stable week-to-week.
 */
export const SOURCE_GROUPS = [
  "Sticky CTA",
  "Packages",
  "Additional Services",
  "Direct",
  "Other",
];
